import React, { useState, useEffect } from "react";
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Eye,
  MoreHorizontal,
  Users,
  DollarSign,
  HardDrive,
  Star,
  CheckCircle,
  XCircle,
  Search,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ApiResponse, PaginationInfo } from "../types/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import { userPlansApi, ApiError } from "../utils/api";

interface UserPlan {
  _id: string;
  planName: string;
  userType: "athlete" | "coach" | "agent" | "ORGANIZATION";
  description: string;
  paymentType: "recurring" | "one_time";
  paymentInterval: "day" | "week" | "month" | "year";
  paymentAmount: number;
  oneTimeFee: number;
  allowedStorage: number;
  disabled: boolean;
  isDefault?: boolean;
  features?: string[];
  supportLevel?: "basic" | "premium" | "enterprise";
  maxUsers?: number;
  createdDate: string;
  updatedAt: string;
  productId: string;
  priceId: string;
  subscribers?: number;
}

export function Packages() {
  const [packages, setPackages] = useState<UserPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<UserPlan | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterUserType, setFilterUserType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "grid">("grid");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Form states for create dialog
  const [newPlan, setNewPlan] = useState({
    planName: "",
    userType: "",
    description: "",
    paymentAmount: 0,
    paymentInterval: "month",
    allowedStorage: 2147483648, // 2GB default
    productId: "",
    priceId: ""
  });
  const [isCreating, setIsCreating] = useState(false);

  // Load packages
  const loadPackages = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: 10,
        sortBy,
        sortOrder,
        ...(filterUserType !== "all" && { userType: filterUserType }),
        ...(filterStatus === "active" && { disabled: false }),
        ...(filterStatus === "disabled" && { disabled: true })
      };

      const response: ApiResponse = await userPlansApi.getAll(params);
      
      if (response.success) {
        setPackages(response.data.plans || []);
        setPagination(response.data.pagination);
      } else {
        throw new Error(response.message || 'Failed to load packages');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message} (${err.status})` 
        : 'Failed to load packages';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const response: ApiResponse = await userPlansApi.getStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  // Initial load
  useEffect(() => {
    loadPackages(currentPage);
    loadStatistics();
  }, [currentPage, sortBy, sortOrder, filterUserType, filterStatus]);

  // Search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        loadPackages(1);
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const formatStorage = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) {
      return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
    }
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatPrice = (amount: number, interval: string) => {
    if (amount === 0) return "Free";
    return `$${amount.toFixed(2)}/${interval}`;
  };

  const getUserTypeColor = (userType: string) => {
    switch (userType.toUpperCase()) {
      case "COACH": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "ATHLETE": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "AGENT": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "ORGANIZATION": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const formatUserType = (userType: string) =>
    userType.charAt(0).toUpperCase() + userType.slice(1).toLowerCase();

  const getSupportLevelColor = (level?: string) => {
    switch (level) {
      case "basic": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      case "premium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "enterprise": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const filteredPackages = packages.filter(pkg => {
    if (!searchTerm) return true;
    return pkg.planName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           pkg.description.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handlePackageAction = async (action: string, packageId: string) => {
    try {
      let response: ApiResponse;
      
      switch (action) {
        case "edit":
          toast.info(`Editing package ${packageId}`);
          break;
          
        case "delete":
          if (window.confirm('Are you sure you want to delete this plan?')) {
            response = await userPlansApi.delete(packageId);
            if (response.success) {
              toast.success('Plan deleted successfully');
              loadPackages(currentPage);
              loadStatistics();
            }
          }
          break;
          
        case "duplicate":
          const planName = prompt('Enter name for the duplicated plan:');
          if (planName) {
            response = await userPlansApi.duplicate(packageId, planName);
            if (response.success) {
              toast.success('Plan duplicated successfully');
              loadPackages(currentPage);
              loadStatistics();
            }
          }
          break;
          
        case "toggle":
          response = await userPlansApi.toggleStatus(packageId);
          if (response.success) {
            const pkg = packages.find(p => p._id === packageId);
            toast.success(`Plan ${pkg?.disabled ? 'enabled' : 'disabled'} successfully`);
            loadPackages(currentPage);
            loadStatistics();
          }
          break;
          
        case "setDefault":
          response = await userPlansApi.setDefault(packageId);
          if (response.success) {
            toast.success('Plan set as default successfully');
            loadPackages(currentPage);
          }
          break;
          
        default:
          break;
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Action failed';
      toast.error(errorMessage);
    }
  };

  const handleCreatePlan = async () => {
    try {
      setIsCreating(true);
      
      // Validate required fields
      if (!newPlan.planName || !newPlan.userType || !newPlan.description) {
        toast.error('Please fill in all required fields');
        return;
      }

      const planData = {
        ...newPlan,
        oneTimeFee: 0,
        paymentType: "recurring",
        disabled: false,
        isDefault: false,
        features: [],
        supportLevel: "basic"
      };

      const response: ApiResponse = await userPlansApi.create(planData);
      
      if (response.success) {
        toast.success('Plan created successfully');
        setShowCreateDialog(false);
        setNewPlan({
          planName: "",
          userType: "",
          description: "",
          paymentAmount: 0,
          paymentInterval: "month",
          allowedStorage: 2147483648,
          productId: "",
          priceId: ""
        });
        loadPackages(currentPage);
        loadStatistics();
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to create plan';
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading && packages.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Package Management</h1>
            <p>Manage subscription plans and pricing tiers.</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-muted rounded w-24"></div>
                  <div className="h-8 bg-muted rounded w-20"></div>
                  <div className="h-3 bg-muted rounded w-32"></div>
                  <div className="h-3 bg-muted rounded w-28"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-muted rounded w-32"></div>
                  <div className="h-8 bg-muted rounded w-24"></div>
                  <div className="h-4 bg-muted rounded w-full"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Package Management</h1>
            <p>Manage subscription plans and pricing tiers.</p>
          </div>
          <Button onClick={() => loadPackages(currentPage)} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const stats = statistics ? [
    {
      title: "Total Plans",
      value: statistics.summary?.totalPlans?.toString() || "0",
      description: "All subscription plans",
      icon: Package
    },
    {
      title: "Active Plans",
      value: statistics.summary?.activePlans?.toString() || "0",
      description: "Currently available",
      icon: CheckCircle
    },
    {
      title: "Disabled Plans",
      value: statistics.summary?.disabledPlans?.toString() || "0",
      description: "Currently disabled",
      icon: XCircle
    },
    {
      title: "Total Revenue",
      value: "$0", // This would need to be calculated from subscriber data
      description: "Monthly recurring revenue",
      icon: DollarSign
    }
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Package Management</h1>
          <p>Manage subscription plans and pricing tiers.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {statistics && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterUserType} onValueChange={setFilterUserType}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="coach">Coach</SelectItem>
              <SelectItem value="athlete">Athlete</SelectItem>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="ORGANIZATION">Organization</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updatedAt">Updated</SelectItem>
              <SelectItem value="planName">Name</SelectItem>
              <SelectItem value="paymentAmount">Price</SelectItem>
              <SelectItem value="userType">Type</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
          >
            Table
          </Button>
        </div>
      </div>

      {isLoading && packages.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        </div>
      )}

      {viewMode === "grid" ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPackages.map((pkg) => (
            <Card key={pkg._id} className={`hover:shadow-lg transition-shadow ${pkg.isDefault ? 'ring-2 ring-primary ring-opacity-50' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">{pkg.planName}</CardTitle>
                      {pkg.isDefault && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getUserTypeColor(pkg.userType)}>
                        {formatUserType(pkg.userType)}
                      </Badge>
                      {pkg.supportLevel && (
                        <Badge className={getSupportLevelColor(pkg.supportLevel)}>
                          {pkg.supportLevel}
                        </Badge>
                      )}
                      {pkg.disabled && <Badge variant="destructive">Disabled</Badge>}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => setSelectedPackage(pkg)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePackageAction("edit", pkg._id)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Plan
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePackageAction("duplicate", pkg._id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {!pkg.isDefault && (
                        <DropdownMenuItem onClick={() => handlePackageAction("setDefault", pkg._id)}>
                          <Star className="h-4 w-4 mr-2" />
                          Set as Default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handlePackageAction("toggle", pkg._id)}>
                        {pkg.disabled ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Enable
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Disable
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handlePackageAction("delete", pkg._id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {formatPrice(pkg.paymentAmount, pkg.paymentInterval)}
                  </div>
                  {pkg.oneTimeFee > 0 && (
                    <div className="text-sm text-muted-foreground">
                      + ${pkg.oneTimeFee} setup fee
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground">{pkg.description}</p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="font-medium">{formatStorage(pkg.allowedStorage)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subscribers</span>
                    <span className="font-medium">{pkg.subscribers || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Billing</span>
                    <span className="font-medium capitalize">{pkg.paymentType}</span>
                  </div>
                </div>

                {pkg.features && pkg.features.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Features</div>
                    <div className="space-y-1">
                      {pkg.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          {feature}
                        </div>
                      ))}
                      {pkg.features.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{pkg.features.length - 3} more features
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan Name</TableHead>
                <TableHead>User Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Storage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPackages.map((pkg) => (
                <TableRow key={pkg._id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pkg.planName}</span>
                        {pkg.isDefault && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                      </div>
                      <p className="text-sm text-muted-foreground">{pkg.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getUserTypeColor(pkg.userType)}>
                      {formatUserType(pkg.userType)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{formatPrice(pkg.paymentAmount, pkg.paymentInterval)}</div>
                      {pkg.oneTimeFee > 0 && (
                        <div className="text-xs text-muted-foreground">
                          + ${pkg.oneTimeFee} setup
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <HardDrive className="h-4 w-4 text-muted-foreground" />
                      {formatStorage(pkg.allowedStorage)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {pkg.disabled ? (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600">Disabled</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-600">Active</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedPackage(pkg)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handlePackageAction("edit", pkg._id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handlePackageAction("duplicate", pkg._id)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handlePackageAction("toggle", pkg._id)}>
                          {pkg.disabled ? "Enable" : "Disable"}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handlePackageAction("delete", pkg._id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, pagination.totalItems)} of {pagination.totalItems} plans
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {currentPage} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Package Details Dialog */}
      {selectedPackage && (
        <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedPackage.planName}
                {selectedPackage.isDefault && <Star className="h-5 w-5 text-yellow-500 fill-current" />}
              </DialogTitle>
              <DialogDescription>
                Complete details for this subscription plan
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">{formatPrice(selectedPackage.paymentAmount, selectedPackage.paymentInterval)}</div>
                      <div className="text-sm text-muted-foreground">Price</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">{selectedPackage.subscribers || 0}</div>
                      <div className="text-sm text-muted-foreground">Subscribers</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">{formatStorage(selectedPackage.allowedStorage)}</div>
                      <div className="text-sm text-muted-foreground">Storage</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold capitalize">{selectedPackage.supportLevel || "Basic"}</div>
                      <div className="text-sm text-muted-foreground">Support Level</div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
                    {selectedPackage.description}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>User Type</Label>
                    <Badge className={getUserTypeColor(selectedPackage.userType)}>
                      {formatUserType(selectedPackage.userType)}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Type</Label>
                    <div className="text-sm capitalize">{selectedPackage.paymentType}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Interval</Label>
                    <div className="text-sm capitalize">{selectedPackage.paymentInterval}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Setup Fee</Label>
                    <div className="text-sm">${selectedPackage.oneTimeFee}</div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="features" className="space-y-4">
                {selectedPackage.features && selectedPackage.features.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPackage.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No features listed for this plan.
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="settings" className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Plan Status</Label>
                      <p className="text-sm text-muted-foreground">Enable or disable this plan</p>
                    </div>
                    <Switch checked={!selectedPackage.disabled} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Default Plan</Label>
                      <p className="text-sm text-muted-foreground">Set as default for this user type</p>
                    </div>
                    <Switch checked={selectedPackage.isDefault} />
                  </div>
                  <div className="space-y-2">
                    <Label>Stripe Product ID</Label>
                    <Input value={selectedPackage.productId} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Stripe Price ID</Label>
                    <Input value={selectedPackage.priceId} disabled />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Plan Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Plan</DialogTitle>
            <DialogDescription>
              Add a new subscription plan to your platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="planName">Plan Name *</Label>
              <Input 
                id="planName" 
                placeholder="Enter plan name"
                value={newPlan.planName}
                onChange={(e) => setNewPlan(prev => ({ ...prev, planName: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="userType">User Type *</Label>
              <Select value={newPlan.userType} onValueChange={(value) => setNewPlan(prev => ({ ...prev, userType: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="athlete">Athlete</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="ORGANIZATION">Organization</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea 
                id="description" 
                placeholder="Plan description"
                value={newPlan.description}
                onChange={(e) => setNewPlan(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentAmount">Price</Label>
                <Input 
                  id="paymentAmount" 
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newPlan.paymentAmount}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, paymentAmount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentInterval">Interval</Label>
                <Select value={newPlan.paymentInterval} onValueChange={(value) => setNewPlan(prev => ({ ...prev, paymentInterval: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productId">Stripe Product ID</Label>
                <Input 
                  id="productId" 
                  placeholder="prod_..."
                  value={newPlan.productId}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, productId: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priceId">Stripe Price ID</Label>
                <Input 
                  id="priceId" 
                  placeholder="price_..."
                  value={newPlan.priceId}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, priceId: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreatePlan} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}